import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, t } from "../../lib/api";
import { useAsync } from "../../lib/hooks";
import { Empty, ErrorNote } from "../../components/ui";
import type { Engagement } from "../Engagement";

interface Term {
  id: string;
  sourceLang: string;
  targetLang: string;
  source: string;
  target: string;
  notes: string | null;
}

/**
 * The client's own words: crop names, internal jargon, system names. They
 * steer both speech recognition and translation, and the list grows each time
 * a reviewer corrects a mistranslation.
 */
export function GlossaryTab({ engagement }: { engagement: Engagement }) {
  const { tenant } = useParams();
  const terms = useAsync(() => api.get<Term[]>(`${t(tenant!)}/engagements/${engagement.id}/glossary`), [tenant, engagement.id]);
  const canEdit = engagement.role !== "interviewer";
  const [f, setF] = useState({ sourceLang: engagement.interviewerLanguages[0]!, targetLang: engagement.intervieweeLanguages[0]!, source: "", target: "", notes: "" });
  const [error, setError] = useState<Error | null>(null);
  const langs = [...new Set([...engagement.interviewerLanguages, ...engagement.intervieweeLanguages])];

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post(`${t(tenant!)}/engagements/${engagement.id}/glossary`, { ...f, notes: f.notes || undefined });
      setF({ ...f, source: "", target: "", notes: "" });
      await terms.reload();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <div className="stack">
      <p className="muted">Terms apply to live recognition and translation within a minute of being saved.</p>
      <div className="panel flush">
        {terms.data?.length === 0 && <Empty title="No terms yet">Add crop names, product names and internal words people use.</Empty>}
        {!!terms.data?.length && (
          <table className="table">
            <thead>
              <tr>
                <th>Term</th>
                <th>Translation</th>
                <th>Languages</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {terms.data.map((x) => (
                <tr key={x.id}>
                  <td><b>{x.source}</b></td>
                  <td>{x.target}</td>
                  <td className="muted">{x.sourceLang} → {x.targetLang}</td>
                  <td className="muted">{x.notes ?? ""}</td>
                  <td>
                    {canEdit && (
                      <button className="btn ghost small" onClick={async () => {
                        await api.del(`${t(tenant!)}/engagements/${engagement.id}/glossary/${x.id}`);
                        await terms.reload();
                      }}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {canEdit && (
        <form className="panel row wrap" onSubmit={add} style={{ alignItems: "flex-end" }}>
          <label className="field" style={{ flex: "1 1 180px" }}>
            <span>Term</span>
            <input className="input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} required />
          </label>
          <label className="field" style={{ flex: "1 1 180px" }}>
            <span>Translation</span>
            <input className="input" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} required />
          </label>
          <label className="field" style={{ flex: "0 1 110px" }}>
            <span>From</span>
            <select className="input" value={f.sourceLang} onChange={(e) => setF({ ...f, sourceLang: e.target.value })}>
              {langs.map((l) => <option key={l}>{l}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: "0 1 110px" }}>
            <span>To</span>
            <select className="input" value={f.targetLang} onChange={(e) => setF({ ...f, targetLang: e.target.value })}>
              {langs.map((l) => <option key={l}>{l}</option>)}
            </select>
          </label>
          <label className="field" style={{ flex: "1 1 180px" }}>
            <span>Note</span>
            <input className="input" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
          </label>
          <button className="btn">Add term</button>
          <ErrorNote error={error} />
        </form>
      )}
    </div>
  );
}
